import React from "react";
import { Select } from "antd";
import { Cloud, FlaskConical, Monitor, Rocket, Server, Smartphone } from "lucide-react";

type CdpTagProps = {
  icon: React.ReactNode;
  label: string;
  id?: string;
  variant?: "tag" | "info";
};

export const CdpTag: React.FC<CdpTagProps> = ({ icon, label, id, variant = "tag" }) => (
  <span
    id={id}
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
      variant === "info"
        ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
        : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-300"
    }`}
  >
    {icon}
    {label}
  </span>
);

type EnvironmentTagProps = {
  environment?: string | null;
  id?: string;
};

export const EnvironmentTag: React.FC<EnvironmentTagProps> = ({ environment, id }) => {
  if (environment !== "production" && environment !== "staging") return null;
  return (
    <CdpTag
      id={id}
      icon={environment === "staging" ? <FlaskConical className="w-3 h-3" /> : <Rocket className="w-3 h-3" />}
      label={environment === "production" ? "Production" : "Staging"}
    />
  );
};

type DirectionTagProps = {
  direction?: string | null;
  id?: string;
};

export const DirectionTag: React.FC<DirectionTagProps> = ({ direction, id }) => {
  if (direction !== "front-end" && direction !== "back-end") return null;
  return (
    <CdpTag
      id={id}
      icon={direction === "back-end" ? <Server className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
      label={direction === "front-end" ? "Front-end" : "Back-end"}
    />
  );
};

type DeliveryModeTagProps = {
  isDeviceMode: boolean;
  id?: string;
};

export const DeliveryModeTag: React.FC<DeliveryModeTagProps> = ({ isDeviceMode, id }) => (
  <CdpTag
    id={id}
    icon={isDeviceMode ? <Smartphone className="w-3 h-3" /> : <Cloud className="w-3 h-3" />}
    label={isDeviceMode ? "Device" : "Cloud"}
    variant="info"
  />
);

/**
 * Hub-frontend-style editor for the `environment` tag field.
 *
 * Defined at module level (not inside a React component) so the function
 * reference is stable across parent re-renders. This prevents rjsf from
 * remounting the widget on every render, which would reset useState.
 *
 * Uses local useState for display — just like hub-frontend's plain <Select>
 * backed by formData useState — so it is completely immune to rjsf's anyOf
 * branch-resolution logic. Any invalid stored value (e.g. a stale `true` or `1`)
 * is silently normalised to null on mount.
 */
const ENVIRONMENT_OPTIONS = [
  { label: "—", value: "" },
  { label: "Production", value: "production" },
  { label: "Staging", value: "staging" },
];
const ENVIRONMENT_VALID = ["production", "staging"];

export const EnvironmentTagEditor: React.FC<{ value?: any; onChange: (v: string | null) => void }> = ({
  value,
  onChange,
}) => {
  const [localValue, setLocalValue] = React.useState<string>(ENVIRONMENT_VALID.includes(value) ? String(value) : "");

  // Normalise any invalid stored value (e.g. `1`, `true`) to null in rjsf formData on mount.
  React.useEffect(() => {
    if (!ENVIRONMENT_VALID.includes(value) && value != null) {
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Select
      style={{ width: "100%" }}
      value={localValue}
      showSearch={false}
      onChange={(v: string) => {
        setLocalValue(v);
        onChange(v || null);
      }}
      options={ENVIRONMENT_OPTIONS}
    />
  );
};

/**
 * Hub-frontend-style editor for the `direction` tag field.
 * See EnvironmentTagEditor for design rationale.
 */
const DIRECTION_OPTIONS = [
  { label: "—", value: "" },
  { label: "Front-end", value: "front-end" },
  { label: "Back-end", value: "back-end" },
];
const DIRECTION_VALID = ["front-end", "back-end"];

export const DirectionTagEditor: React.FC<{ value?: any; onChange: (v: string | null) => void }> = ({
  value,
  onChange,
}) => {
  const [localValue, setLocalValue] = React.useState<string>(DIRECTION_VALID.includes(value) ? String(value) : "");

  // Normalise any invalid stored value to null in rjsf formData on mount.
  React.useEffect(() => {
    if (!DIRECTION_VALID.includes(value) && value != null) {
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Select
      style={{ width: "100%" }}
      value={localValue}
      showSearch={false}
      onChange={(v: string) => {
        setLocalValue(v);
        onChange(v || null);
      }}
      options={DIRECTION_OPTIONS}
    />
  );
};
